import React, { useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { ensureThemeConsistency, getCurrentTheme } from '../utils/themeUtils';

/**
 * Theme consistency checker component that ensures proper theme application
 * across all pages and components
 */
const ThemeConsistencyChecker = ({ children }) => {
  const { theme } = useTheme();
  const [inconsistencies, setInconsistencies] = useState([]);
  const [isChecking, setIsChecking] = useState(false);

  /**
   * Check for theme inconsistencies in the DOM
   */
  const checkThemeConsistency = () => {
    const issues = [];
    
    // Check if root element has correct theme classes
    const root = document.documentElement;
    const currentTheme = getCurrentTheme();
    
    if (!root.classList.contains(currentTheme)) {
      issues.push({
        type: 'root_class_missing',
        message: `Root element missing ${currentTheme} class`,
        element: root,
        fix: () => root.classList.add(currentTheme)
      });
    }
    
    if (root.getAttribute('data-theme') !== currentTheme) {
      issues.push({
        type: 'root_attribute_missing',
        message: `Root element missing data-theme="${currentTheme}" attribute`,
        element: root,
        fix: () => root.setAttribute('data-theme', currentTheme)
      });
    }
    
    // Check for hardcoded theme classes that should be dynamic
    const hardcodedSelectors = [
      'bg-gray-900:not(.dark\\:bg-gray-900)',
      'text-white:not(.dark\\:text-white)',
      'bg-white:not(.dark\\:bg-white)',
      'text-gray-900:not(.dark\\:text-gray-900)'
    ];
    
    hardcodedSelectors.forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          // Skip elements that already have proper dark mode classes
          const classList = Array.from(element.classList);
          const hasDarkVariant = classList.some(cls => cls.startsWith('dark:'));
          
          if (!hasDarkVariant) {
            issues.push({
              type: 'hardcoded_theme_class',
              message: `Element has hardcoded theme class without dark variant: ${selector}`,
              element,
              fix: null // Manual fix required
            });
          }
        });
      } catch (error) {
        // Ignore invalid selectors
      }
    });
    
    // Check for missing theme transitions
    const elementsNeedingTransitions = document.querySelectorAll(
      '.bg-white, .bg-gray-50, .bg-gray-100, .bg-gray-800, .bg-gray-900, ' +
      '.text-white, .text-gray-900, .text-gray-600, .text-gray-300, ' +
      '.border-gray-200, .border-gray-300, .border-gray-600, .border-gray-700'
    );
    
    elementsNeedingTransitions.forEach(element => {
      const computedStyle = window.getComputedStyle(element);
      const transitionProp = computedStyle.transitionProperty || '';
      const transitionDuration = computedStyle.transitionDuration || '';

      const classList = Array.from(element.classList || []);
      const hasTailwindTransitionClass = classList.some(cls =>
        cls === 'transition' || cls === 'transition-colors' || cls.startsWith('duration-') || cls.startsWith('ease-')
      );

      const hasTransition = hasTailwindTransitionClass ||
        transitionProp.includes('all') ||
        transitionProp.includes('background-color') ||
        transitionProp.includes('color') ||
        transitionProp.includes('border-color') ||
        transitionDuration.split(',').some(d => parseFloat(d) > 0);

      if (!hasTransition) {
        issues.push({
          type: 'missing_transition',
          message: 'Element may be missing theme transition',
          element,
          fix: null
        });
      }
    });
    
    return issues;
  };

  /**
   * Auto-fix theme inconsistencies where possible
   */
  const autoFixIssues = (issues) => {
    let fixedCount = 0;
    
    issues.forEach(issue => {
      if (issue.fix && typeof issue.fix === 'function') {
        try {
          issue.fix();
          fixedCount++;
        } catch (error) {
          console.warn('Failed to auto-fix theme issue:', error);
        }
      }
    });
    
    return fixedCount;
  };

  /**
   * Run theme consistency check
   */
  const runConsistencyCheck = async () => {
    setIsChecking(true);
    
    // Ensure basic theme consistency first
    ensureThemeConsistency();
    
    // Wait for DOM updates
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Check for issues
    const issues = checkThemeConsistency();
    setInconsistencies(issues);
    
    // Auto-fix issues where possible
    if (issues.length > 0) {
      const fixedCount = autoFixIssues(issues);
      
      if (fixedCount > 0) {
        if (process.env.NODE_ENV === 'development') { console.log('Auto-fixed ' + fixedCount + ' theme consistency issues'); }
        
        // Re-check after fixes
        setTimeout(() => {
          const remainingIssues = checkThemeConsistency();
          setInconsistencies(remainingIssues);
        }, 100);
      }
    }
    
    setIsChecking(false);
  };

  // Run consistency check when theme changes
  useEffect(() => {
    runConsistencyCheck();
  }, [theme]);

  // Run consistency check on mount and when DOM changes
  useEffect(() => {
    runConsistencyCheck();
    
    // Set up mutation observer to detect DOM changes
    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;
      
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if any added nodes have theme-related classes
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node;
              const classList = Array.from(element.classList || []);
              const hasThemeClasses = classList.some(cls => 
                cls.includes('bg-') || cls.includes('text-') || cls.includes('border-') ||
                cls.includes('dark:') || cls.includes('light:')
              );
              
              if (hasThemeClasses) {
                shouldCheck = true;
              }
            }
          });
        }
      });
      
      if (shouldCheck) {
        // Debounce the check to avoid excessive calls
        clearTimeout(window.themeCheckTimeout);
        window.themeCheckTimeout = setTimeout(runConsistencyCheck, 500);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'data-theme']
    });
    
    return () => {
      observer.disconnect();
      clearTimeout(window.themeCheckTimeout);
    };
  }, []);

  // Development mode: show consistency issues in console
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && inconsistencies.length > 0) {
      console.group('Theme Consistency Issues');
      inconsistencies.forEach((issue, index) => {
        console.warn(`${index + 1}. ${issue.message}`, issue.element);
      });
      console.groupEnd();
    }
  }, [inconsistencies]);

  return (
    <>
      {children}
      {/* Development mode: show consistency checker UI */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4 z-50">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                isChecking ? 'bg-yellow-500 animate-pulse' :
                inconsistencies.length === 0 ? 'bg-green-500' : 'bg-red-500'
              }`}></div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Theme: {inconsistencies.length === 0 ? 'Consistent' : `${inconsistencies.length} issues`}
              </span>
              <button
                onClick={runConsistencyCheck}
                className="text-xs px-2 py-1 bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors dark:text-white"
                disabled={isChecking}
              >
                {isChecking ? 'Checking...' : 'Check'}
              </button>
            </div>
            {inconsistencies.length > 0 && (
              <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                <details>
                  <summary className="cursor-pointer hover:text-gray-900 dark:hover:text-white dark:text-white">
                    View Issues
                  </summary>
                  <ul className="mt-1 space-y-1 max-h-32 overflow-y-auto">
                    {inconsistencies.slice(0, 10).map((issue, index) => (
                      <li key={index} className="truncate">
                        {issue.message}
                      </li>
                    ))}
                    {inconsistencies.length > 10 && (
                      <li className="text-gray-500">
                        ...and {inconsistencies.length - 10} more
                      </li>
                    )}
                  </ul>
                </details>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ThemeConsistencyChecker;


